import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../api'
import { useAuth } from '../AuthContext'

export default function useVisitBasket() {
  const { token, freshLoginRef } = useAuth()
  const tokenRef = useRef(token)
  useEffect(() => { tokenRef.current = token }, [token])

  const [saved, setSaved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('lyannaj_basket') || '[]')
    } catch { return [] }
  })

  // `pret` = le serveur a REPONDU pour CE token (ou il n'y a rien a lui
  // demander). Sans ce drapeau, un ecran ne peut pas distinguer « aucun favori »
  // de « favoris pas encore arrives » : c'est ce qui faisait clignoter l'ecran
  // « Aucun favori » a la premiere ouverture de l'onglet (S28, ecart E-12).
  //
  // On memorise le TOKEN servi, pas un simple booleen : au moment ou l'on se
  // connecte, `token` devient non nul des le rendu, alors qu'un booleen ne
  // repasserait a false que dans l'effet, apres le premier affichage. Cette
  // fenetre suffisait a laisser reapparaitre l'ecran vide.
  const [tokenServi, setTokenServi] = useState(null)
  const pret = !token || tokenServi === token

  // Sync au login : merge localStorage → DB, puis charger la liste complete
  // Sur restauration de session (rechargement de page), toujours charger depuis le serveur
  useEffect(() => {
    let vivant = true
    if (token) {
      const isFreshLogin = freshLoginRef.current
      // Ne pas reset freshLoginRef ici — useProductFavorites en a aussi besoin
      const localFavorites = JSON.parse(localStorage.getItem('lyannaj_basket') || '[]')
      // Sync localStorage → DB uniquement lors d'un vrai login (favoris anonymes a fusionner)
      // Sinon, le serveur est la source de verite (sync multi-appareil)
      const request = (isFreshLogin && localFavorites.length > 0)
        ? api.favorites.sync(localFavorites, token)
        : api.favorites.get(token)
      request
        .then(dbFavorites => {
          if (!vivant) return
          setSaved(dbFavorites)
          localStorage.setItem('lyannaj_basket', JSON.stringify(dbFavorites))
        })
        .catch(() => {}) // Fallback localStorage si API indisponible
        // Meme en cas d'echec, on a fini d'attendre : on retombe sur
        // localStorage plutot que de laisser un chargement infini.
        .finally(() => { if (vivant) setTokenServi(token) })
    } else {
      // Logout : relire localStorage (vide par AuthContext)
      try {
        setSaved(JSON.parse(localStorage.getItem('lyannaj_basket') || '[]'))
      } catch { setSaved([]) }
      setTokenServi(null)
    }
    return () => { vivant = false }
  }, [token])

  // Rafraichir depuis la DB quand l'onglet redevient visible (sync multi-appareil)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && tokenRef.current) {
        api.favorites.get(tokenRef.current)
          .then(dbFavorites => {
            setSaved(dbFavorites)
            localStorage.setItem('lyannaj_basket', JSON.stringify(dbFavorites))
          })
          .catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  const toggle = useCallback((standOrgId) => {
    setSaved(prev => {
      const isRemoving = prev.includes(standOrgId)
      const next = isRemoving
        ? prev.filter(id => id !== standOrgId)
        : [...prev, standOrgId]
      localStorage.setItem('lyannaj_basket', JSON.stringify(next))
      // Appel API en arriere-plan si connecte
      const t = tokenRef.current
      if (t) {
        (isRemoving ? api.favorites.remove(standOrgId, t) : api.favorites.add(standOrgId, t)).catch(() => {})
      }
      return next
    })
  }, [])

  const isSaved = useCallback((standOrgId) => saved.includes(standOrgId), [saved])

  const clear = useCallback(() => {
    setSaved([])
    localStorage.removeItem('lyannaj_basket')
    const t = tokenRef.current
    if (t) {
      api.favorites.clearAll(t).catch(() => {})
    }
  }, [])

  return { saved, toggle, isSaved, count: saved.length, clear, pret }
}
